import type { CompetitorIntelligence, OpportunityItem } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, Brain, Lightbulb } from "lucide-react";

const scoreColor = (s: number) => s >= 70 ? "text-success" : s >= 40 ? "text-warning" : "text-destructive";

interface AnalyzedScores {
  contentDepth: number;
  keywordCoverage: number;
  technicalSEO: number;
  backlinkStrength: number;
  aiVisibility: number;
}

const categoryConfig = {
  content: { label: "Content", icon: Target, className: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  authority: { label: "Authority", icon: TrendingUp, className: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  "ai-visibility": { label: "AI Visibility", icon: Brain, className: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
};

const impactConfig = {
  high: { label: "High Impact", className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  medium: { label: "Medium Impact", className: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  low: { label: "Low Impact", className: "bg-muted text-muted-foreground border-border" },
};

function isOpportunityItem(o: string | OpportunityItem): o is OpportunityItem {
  return typeof o === "object" && "text" in o && "category" in o;
}

export const CompetitorTab = ({ data, analyzedUrl, analyzedScores }: { data: CompetitorIntelligence; analyzedUrl: string; analyzedScores: AnalyzedScores }) => (
  <div className="space-y-4 mt-4">
    <Card className="border-border bg-card overflow-hidden">
      <CardHeader><CardTitle className="text-base">Competitor Comparison</CardTitle></CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead>Site</TableHead>
                <TableHead className="text-center">Content</TableHead>
                <TableHead className="text-center">Keywords</TableHead>
                <TableHead className="text-center">Technical</TableHead>
                <TableHead className="text-center">Backlinks</TableHead>
                <TableHead className="text-center">AI Visibility</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-border bg-primary/5 border-l-2 border-l-primary">
                <TableCell>
                  <div className="font-medium text-sm text-primary">Your Site</div>
                  <div className="text-xs text-muted-foreground truncate max-w-[200px]">{analyzedUrl}</div>
                </TableCell>
                <TableCell className={`text-center font-mono font-bold ${scoreColor(analyzedScores.contentDepth)}`}>{analyzedScores.contentDepth}</TableCell>
                <TableCell className={`text-center font-mono font-bold ${scoreColor(analyzedScores.keywordCoverage)}`}>{analyzedScores.keywordCoverage}</TableCell>
                <TableCell className={`text-center font-mono font-bold ${scoreColor(analyzedScores.technicalSEO)}`}>{analyzedScores.technicalSEO}</TableCell>
                <TableCell className={`text-center font-mono font-bold ${scoreColor(analyzedScores.backlinkStrength)}`}>{analyzedScores.backlinkStrength}</TableCell>
                <TableCell className={`text-center font-mono font-bold ${scoreColor(analyzedScores.aiVisibility)}`}>{analyzedScores.aiVisibility}</TableCell>
              </TableRow>
              {data.competitors.map((c) => (
                <TableRow key={c.url} className="border-border">
                  <TableCell>
                    <div className="font-medium text-sm text-foreground">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">{c.url}</div>
                  </TableCell>
                  <TableCell className={`text-center font-mono font-bold ${scoreColor(c.contentDepth)}`}>{c.contentDepth}</TableCell>
                  <TableCell className={`text-center font-mono font-bold ${scoreColor(c.keywordCoverage)}`}>{c.keywordCoverage}</TableCell>
                  <TableCell className={`text-center font-mono font-bold ${scoreColor(c.technicalSEO)}`}>{c.technicalSEO}</TableCell>
                  <TableCell className={`text-center font-mono font-bold ${scoreColor(c.backlinkStrength)}`}>{c.backlinkStrength}</TableCell>
                  <TableCell className={`text-center font-mono font-bold ${scoreColor(c.aiVisibility)}`}>{c.aiVisibility}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <GapCard icon={Target} title="Content Gaps" items={data.contentGaps} />
      <GapCard icon={TrendingUp} title="Authority Gaps" items={data.authorityGaps} />
      <GapCard icon={Brain} title="AI Visibility Gaps" items={data.aiVisibilityGaps} />
    </div>

    {data.opportunities.length > 0 && (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-base text-primary flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Improvement Opportunities
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.opportunities.map((o, i) =>
            isOpportunityItem(o) ? (
              <div key={i} className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    {(() => {
                      const config = categoryConfig[o.category];
                      const Icon = config.icon;
                      return (
                        <Badge variant="outline" className={`shrink-0 text-[10px] gap-1 ${config.className}`}>
                          <Icon className="w-3 h-3" />
                          {config.label}
                        </Badge>
                      );
                    })()}
                    <span className="text-sm text-foreground">{o.text}</span>
                  </div>
                  {o.impact && (
                    <Badge variant="outline" className={`shrink-0 text-[10px] ${impactConfig[o.impact].className}`}>
                      {impactConfig[o.impact].label}
                    </Badge>
                  )}
                </div>
                <div className="ml-[88px] bg-muted rounded p-2 text-xs italic text-muted-foreground">
                  💡 {o.example}
                </div>
              </div>
            ) : (
              <div key={i} className="text-sm text-foreground flex items-start gap-2">
                <Badge variant="outline" className="shrink-0 text-[10px]">{i + 1}</Badge>
                {o}
              </div>
            )
          )}
        </CardContent>
      </Card>
    )}
  </div>
);

const GapCard = ({ icon: Icon, title, items }: { icon: any; title: string; items: string[] }) => (
  <Card className="border-border bg-card">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-accent" />{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-1.5">
      {items.map((item, i) => (
        <p key={i} className="text-xs text-muted-foreground">• {item}</p>
      ))}
    </CardContent>
  </Card>
);
